import { styled, css } from '../../../../modules';
import { keyframes } from 'styled-components';

export const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    background-color: #ffffff;
    overflow: hidden;
`;

export const LogoSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e5e5;
    flex-shrink: 0;
`;

export const LogoImage = styled.img`
    max-height: 52px;
    max-width: 200px;
    object-fit: contain;
`;

export const ScrollContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    width: 100%;
    overflow-y: auto;
    padding: 28px 20px 24px;
    gap: 20px;
`;

export const HeaderBlock = styled.div`
    width: 100%;
`;

export const Eyebrow = styled.div`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.4px;
    color: #718096;
    text-transform: uppercase;
`;

export const Title = styled.h1<{ $color: string }>`
    font-size: 23px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    margin: 6px 0 0;
    line-height: 1.2;
    letter-spacing: -0.2px;
`;

export const Subtitle = styled.p`
    font-size: 14px;
    color: #4a5568;
    margin: 8px 0 0;
    line-height: 1.5;

    strong {
        font-weight: 700;
        color: #2d3748;
    }
`;

export const SectionCaption = styled.div`
    width: 100%;
    font-size: 12px;
    font-weight: 700;
    color: #718096;
    letter-spacing: 0.2px;
    text-transform: uppercase;
    margin-bottom: -8px;
`;

export const DataCardContainer = styled.div<{ $accent?: boolean; $color?: string }>`
    width: 100%;
    background-color: #ffffff;
    border: ${({ $accent, $color }) => ($accent ? `1.5px solid ${$color}` : '1px solid #edf2f7')};
    border-radius: 14px;
    box-sizing: border-box;
`;

export const CardHeader = styled.div<{ $color: string }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 16px;
    color: ${({ $color }) => $color};
    font-size: 16px;
    font-weight: 700;
    border-bottom: 1px solid #edf2f7;
`;

export const BankHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid #edf2f7;
`;

export const BankIconTile = styled.div<{ $color: string }>`
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background-color: ${({ $color }) => $color}14;
    color: ${({ $color }) => $color};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const BankHeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
`;

export const BankName = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: #2d3748;
    line-height: 1.25;
`;

export const BankCaption = styled.span`
    font-size: 11.5px;
    color: #718096;
    line-height: 1.3;
`;

export const DataRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 13px 16px;
    border-top: 1px solid #edf2f7;

    &:first-child {
        border-top: none;
    }
`;

export const RowLabel = styled.span<{ $emphasis?: boolean }>`
    font-size: ${({ $emphasis }) => ($emphasis ? '14px' : '13px')};
    font-weight: ${({ $emphasis }) => ($emphasis ? 600 : 400)};
    color: #718096;
    flex: 1;
    padding-right: 8px;
`;

export const RowValue = styled.span<{ $color: string; $emphasis?: boolean }>`
    font-size: ${({ $emphasis }) => ($emphasis ? '17px' : '14px')};
    font-weight: ${({ $emphasis }) => ($emphasis ? 700 : 600)};
    color: ${({ $color, $emphasis }) => ($emphasis ? $color : '#2d3748')};
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const Footnote = styled.p`
    width: 100%;
    font-size: 11.5px;
    color: #a0aec0;
    margin: -8px 0 0;
    line-height: 1.5;
`;

export const InfoCallout = styled.div<{ $color: string }>`
    width: 100%;
    background-color: ${({ $color }) => $color}22;
    border-radius: 8px;
    border: 1px solid ${({ $color }) => $color}55;
    padding: 12px 14px;
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: flex-start;
    box-sizing: border-box;
`;

export const IconWrapper = styled.div<{ $color: string }>`
    color: ${({ $color }) => $color};
    flex-shrink: 0;
    display: flex;
`;

export const InfoText = styled.p<{ $color: string }>`
    font-size: 13.5px;
    color: #4a5568;
    margin: 0;
    line-height: 1.5;

    strong {
        font-weight: 700;
        color: #2d3748;
    }

    a {
        color: ${({ $color }) => $color};
        text-decoration: underline;
        cursor: pointer;
    }
`;

export const CheckboxWrapper = styled.div`
    width: 100%;
`;

export const Footer = styled.footer`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 16px 20px;
    border-top: 1px solid #e5e5e5;
    background-color: #ffffff;
    flex-shrink: 0;
`;

export const SubmitButton = styled.button<{ $backgroundColor: string; $disabled: boolean }>`
    width: 100%;
    padding: 16px 20px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
    background-color: ${({ $backgroundColor, $disabled }) => ($disabled ? '#cbd5e0' : $backgroundColor)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;

export const OutlineButton = styled.button<{ $color: string; $disabled: boolean }>`
    width: 100%;
    padding: 15px 20px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    background-color: ${({ $color, $disabled }) => $disabled ? '#fff' : `${$color}33`};
    border: 1px solid ${({ $color, $disabled }) => ($disabled ? '#e2e8f0' : $color)};
    color: ${({ $color, $disabled }) => ($disabled ? '#a0aec0' : $color)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
        opacity: 0.85;
    }
`;

export const FolderBackBar = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 12px 16px 0;
    flex-shrink: 0;
`;

export const FolderBackButton = styled.button<{ $color: string }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: none;
    background: transparent;
    color: ${({ $color }) => $color};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 0;
    font-family: inherit;
`;

export const FolderDecisionRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    width: 100%;
`;

export const FolderCurrentDecision = styled.div<{ $color: string }>`
    width: 100%;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: ${({ $color }) => $color};
`;

export const Merge = styled.div<{ $color: string }>`
    width: 100%;
    border-radius: 16px;
    padding: 18px;
    box-sizing: border-box;
    border: 1.5px solid ${({ $color }) => $color}55;
    background: linear-gradient(160deg, ${({ $color }) => $color}33, #ffffff 80%);
`;

export const MergeMoveRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
`;

export const MergeNode = styled.div<{ $align?: 'left' | 'right' }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: ${({ $align }) => ($align === 'right' ? 'right' : 'left')};
`;

export const MergeNodeLabel = styled.span`
    font-size: 11px;
    color: #718096;
    line-height: 1.3;
`;

export const MergeNodeValue = styled.span`
    font-size: 13.5px;
    font-weight: 700;
    color: #2d3748;
    line-height: 1.3;
`;

export const MergeArrow = styled.div<{ $color: string }>`
    color: ${({ $color }) => $color};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const MergeTroco = styled.div<{ $color: string }>`
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid ${({ $color }) => $color}33;
`;

export const MergeTrocoLead = styled.div<{ $color: string }>`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

export const MergeTrocoValue = styled.div<{ $color: string }>`
    font-size: 34px;
    font-weight: 800;
    color: ${({ $color }) => $color};
    line-height: 1.1;
    margin-top: 6px;
    font-variant-numeric: tabular-nums;

    sup {
        font-size: 16px;
        font-weight: 700;
        vertical-align: super;
    }
`;

export const MergeStatsGrid = styled.div<{ $color: string }>`
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid ${({ $color }) => $color}33;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

export const MergeStatItem = styled.div<{ $full?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 2px;
    grid-column: ${({ $full }) => ($full ? '1 / -1' : 'auto')};
`;

export const MergeStatLabel = styled.span`
    font-size: 11.5px;
    color: #718096;
    line-height: 1.3;
`;

export const MergeStatValue = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: #2d3748;
    line-height: 1.3;
    font-variant-numeric: tabular-nums;
`;

export const MergeFootnote = styled.p`
    font-size: 11.5px;
    color: #718096;
    margin: 12px 0 0;
    line-height: 1.4;
`;

export const Accordion = styled.div<{ $color: string; $open: boolean }>`
    width: 100%;
    border: 1px solid ${({ $color, $open }) => ($open ? `${$color}55` : '#edf2f7')};
    border-radius: 14px;
    overflow: hidden;
    flex-shrink: 0;
    transition: border-color 0.2s ease;
`;

export const AccordionHeader = styled.button<{ $color: string }>`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
`;

export const AccordionIconTile = styled.span<{ $color: string }>`
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background-color: ${({ $color }) => $color}14;
    color: ${({ $color }) => $color};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const AccordionTitleBlock = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1px;
`;

export const AccordionTitle = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: #2d3748;
    line-height: 1.25;
`;

export const AccordionSubtitle = styled.span`
    font-size: 11.5px;
    color: #718096;
    line-height: 1.3;
`;

export const AccordionChevron = styled.span<{ $color: string; $open: boolean }>`
    color: #718096;
    transition: transform 0.25s ease;
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
    display: flex;
    flex-shrink: 0;
`;

export const AccordionBody = styled.div<{ $open: boolean }>`
    display: grid;
    grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
    transition: grid-template-rows 0.3s ease;

    > .inner {
        overflow: hidden;
    }
`;

export const AccordionInner = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 4px 14px 16px;
`;

export const InnerCaption = styled.div`
    font-size: 11.5px;
    font-weight: 700;
    color: #718096;
    letter-spacing: 0.3px;
    text-transform: uppercase;
`;

export const InnerCardRows = styled.div`
    border: 1px solid #edf2f7;
    border-radius: 12px;
`;

export const InnerFootnote = styled.p`
    font-size: 11.5px;
    color: #a0aec0;
    margin: 0;
    line-height: 1.5;
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const slideUp = keyframes`
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
`;

export const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 100;
    ${css`
        animation: ${fadeIn} 0.3s ease forwards;
    `}
`;

export const ModalSheet = styled.div`
    background: #ffffff;
    border-radius: 20px 20px 0 0;
    padding: 28px 24px 36px;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    max-height: 80vh;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
    gap: 16px;
    ${css`
        animation: ${slideUp} 0.3s ease forwards;
    `}
`;

export const SheetHandle = styled.div`
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background-color: #ddd;
    align-self: center;
    margin-bottom: 4px;
    flex-shrink: 0;
`;

export const ModalTitle = styled.h2<{ $color: string }>`
    font-size: 20px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    margin: 0;
`;

export const ModalText = styled.p`
    font-size: 14px;
    color: #4a5568;
    margin: 0;
    line-height: 1.6;
    text-align: center;

    ul {
        text-align: start;
    }
`;

export const ModalButtonRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 4px;
`;
